import React, { useState, useEffect } from 'react';
import { 
  Button, 
  makeStyles, 
  Typography, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  FormControlLabel,
  Checkbox,
  Box
} from '@material-ui/core';
import { getApplicationStages, getApplicationProgressByApplicationId, updateFundingApplication, updateApplicationProgressByApplicationId } from '../../services/funding.service';
import { FUNDING_APPLICATION_CONSTANT } from '../../constants/common';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(2),
  },
  stepper: {
    padding: theme.spacing(3),
    background: 'transparent',
  },
  completedStep: {
    color: theme.palette.success.main,
  },
  pendingStep: {
    color: theme.palette.text.disabled,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  dialogPaper: {
    minWidth: '800px',
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  batchActions: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  stepLabel: {
    width: '100%',
    color:'red'
  },
  stepIcon: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontWeight: 'bold',
    fontSize: '0.75rem',
  },
  completedIcon: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
  },
  pendingIcon: {
    backgroundColor: theme.palette.action.disabled,
    color: theme.palette.common.white,
  },
  saveButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

function StepNumberIcon({ active, completed, stepNumber }) {
  const classes = useStyles();
  
  return (
    <div className={`${classes.stepIcon} ${completed ? classes.completedIcon : classes.pendingIcon}`}>
      {stepNumber}
    </div>
  );
}

const FundingApplicationProgress = ({ data }) => {
  const classes = useStyles();
  const [openModal, setOpenModal] = useState(false);
  const [stages, setStages] = useState([]);
  const [stageStatuses, setStageStatuses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [initialStatuses, setInitialStatuses] = useState({});

  const onClick = () => setOpenModal(true);
  
  const handleClose = () => setOpenModal(false);

  useEffect(() => {
    if (!openModal) return;

    const fetchStagesAndProgress = async () => {
      try {
        setIsLoading(true);
        
        const { result: allStages } = await getApplicationStages({ 
          program_id: data.program_id 
        });

               
        const { result: completed } = await getApplicationProgressByApplicationId({ 
          program_id: data.program_id,
          application_id: data.application_id 
        });

        const sortedStages = allStages.sort((a, b) => a.order - b.order);
        setStages(sortedStages);
        
        const statusMap = {};
        sortedStages.forEach(stage => {
          const completedStage = completed.find(c => c.stage_id === stage.stage_id);
          statusMap[stage.stage_id] = completedStage ? completedStage.status : FUNDING_APPLICATION_CONSTANT.STATUS.PENDING;
        });
        
        setStageStatuses(statusMap);
        setInitialStatuses(statusMap); 
      } catch (err) {
        setError(err.message || 'Failed to load application progress');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStagesAndProgress();
  }, [openModal, data]);

  const handleStatusChange = (stageId) => {
    setStageStatuses(prev => ({
      ...prev,
      [stageId]: prev[stageId] === FUNDING_APPLICATION_CONSTANT.STATUS.COMPLETED ? FUNDING_APPLICATION_CONSTANT.STATUS.PENDING : FUNDING_APPLICATION_CONSTANT.STATUS.COMPLETED
    }));
  };

  const toggleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    
    const newStatuses = { ...stageStatuses };
    Object.keys(newStatuses).forEach(stageId => {
      newStatuses[stageId] = newSelectAll ? FUNDING_APPLICATION_CONSTANT.STATUS.COMPLETED : FUNDING_APPLICATION_CONSTANT.STATUS.PENDING;
    });
    
    setStageStatuses(newStatuses);
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      // Prepare updates array - only include stages that have changed
      const updates = Object.entries(stageStatuses)
        .filter(([stageId, status]) => initialStatuses[stageId] !== status)
        .map(([stageId, status]) => {
          const payload = {
            stage_id: stageId,
            status,
            user_id: data.patient_id
          };
          
          return payload;
        });
  
      if (updates.length === 0) {
        handleClose();
        return;
      }

      const response = await updateApplicationProgressByApplicationId({
        program_id: data.program_id,
        application_id: data.application_id,
        payload: {
          task_progress: updates
        }
      });

      if (response) {
        window.location.reload();
      }
  
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to update stages');
    } finally {
      setIsSaving(false);
    }
  };

  const getStepStatusClass = (stageId) => {
    return stageStatuses[stageId] === FUNDING_APPLICATION_CONSTANT.STATUS.COMPLETED 
      ? classes.completedStep 
      : classes.pendingStep;
  };

  const hasChanges = JSON.stringify(stageStatuses) !== JSON.stringify(initialStatuses);

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={onClick}
        style={{ marginRight: 8, marginBottom: 10 }}
      >
        Progress
      </Button>

      <Dialog
        open={openModal}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>Funding Application Progress Tracking</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : error ? (
            <Card className={classes.root}>
              <CardContent>
                <Typography color="error">{error}</Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              <Box className={classes.batchActions}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectAll}
                      onChange={toggleSelectAll}
                      color="primary"
                    />
                  }
                  label="Select All Completed"
                />
              </Box>

              <Stepper orientation="vertical" className={classes.stepper}>
                {stages.map((stage, index) => (
                  <Step key={stage.stage_id} completed={stageStatuses[stage.stage_id] === FUNDING_APPLICATION_CONSTANT.STATUS.COMPLETED}>
                    <StepLabel
                      className={classes.stepLabel}
                      StepIconComponent={(props) => (
                        <StepNumberIcon 
                          {...props} 
                          stepNumber={index + 1} 
                          completed={stageStatuses[stage.stage_id] === FUNDING_APPLICATION_CONSTANT.STATUS.COMPLETED}
                        />
                      )}
                    >
                      <Grid container alignItems="center">
                        {/* Stage name */}
                        <Grid item xs={12} sm={6}>
                          <Typography className={getStepStatusClass(stage.stage_id)}>
                            {stage.stage_name.replace(/-/g, ' ').toUpperCase()}
                          </Typography>
                        </Grid>
                        
                        {/* Status checkboxes */}
                        <Grid item xs={12} sm={6}>
                          <div className={classes.checkboxGroup}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={stageStatuses[stage.stage_id] === FUNDING_APPLICATION_CONSTANT.STATUS.COMPLETED}
                                  onChange={() => handleStatusChange(stage.stage_id, FUNDING_APPLICATION_CONSTANT.STATUS.COMPLETED)}
                                  color="primary"
                                />
                              }
                              label="Completed"
                            />
                          </div>
                        </Grid>
                      </Grid>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              
              {Object.values(stageStatuses).every(status => status === FUNDING_APPLICATION_CONSTANT.STATUS.COMPLETED) && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      All stages completed!
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions className={classes.saveButtonContainer}>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveChanges}
            disabled={isSaving || !hasChanges}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Save All Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FundingApplicationProgress;